import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cacheQuoteDetail, cacheQuoteOptions, getQuoteDetail, getQuoteOptions,
  quoteFilterParams, quoteQueryParams, requestQuotes, isCurrentQuoteSession,
} from '../utils/quoteRequests';

const emptyPagination = { current_page: 1, per_page: 50, total: 0, last_page: 1 };
const errorMessage = error => error.response?.data?.message || error.message || 'Unable to load quotes.';

export default function useQuoteListing(token) {
  const [filters, setFilters] = useState({});
  const [query, setQuery] = useState({ token, page: 1, filters: {}, sort: { key: null, direction: null } });
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState({ rows: [], pagination: emptyPagination, loading: true, error: '', token });
  useEffect(() => {
    setFilters({});
    setQuery(previous => previous.token === token ? previous : { token, page: 1, filters: {}, sort: { key: null, direction: null } });
  }, [token]);
  const filterKey = JSON.stringify(quoteFilterParams(filters));
  const appliedFilterKey = JSON.stringify(query.filters);

  useEffect(() => {
    if (filterKey === appliedFilterKey) return undefined;
    const timeout = setTimeout(() => {
      setQuery(previous => ({ ...previous, page: 1, filters: JSON.parse(filterKey) }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [filterKey, appliedFilterKey]);

  const params = useMemo(() => quoteQueryParams(query), [query]);
  useEffect(() => {
    if (query.token !== token) return undefined;
    let active = true;
    setResult(previous => previous.token === token
      ? { ...previous, loading: true, error: '' }
      : { rows: [], pagination: emptyPagination, loading: true, error: '', token });
    if (!token) {
      setResult({ rows: [], pagination: emptyPagination, loading: false, error: 'Please sign in to view quotes.', token });
      return undefined;
    }
    const request = requestQuotes(token, '', params);
    request.promise.then(payload => {
      if (!active || !isCurrentQuoteSession(token)) return;
      const pagination = { ...emptyPagination, ...payload.pagination };
      if (query.page > Math.max(1, pagination.last_page)) {
        setQuery(previous => ({ ...previous, page: Math.max(1, pagination.last_page) }));
        return;
      }
      setResult({ rows: (payload.data || []).filter(Boolean), pagination, loading: false, error: '', token });
    }).catch(error => {
      if (active && isCurrentQuoteSession(token)) setResult(previous => ({ ...previous, loading: false, error: errorMessage(error), token }));
    });
    return () => { active = false; request.release(); };
  }, [token, params, query.page, query.token, reload]);

  const setPage = useCallback(page => {
    setQuery(previous => previous.page === page ? previous : { ...previous, page });
  }, []);
  const sortBy = useCallback(key => {
    setQuery(previous => {
      const direction = previous.sort.key === key
        ? (previous.sort.direction === 'asc' ? 'desc' : null) : 'asc';
      return { ...previous, page: 1, sort: { key: direction ? key : null, direction } };
    });
  }, []);
  const retry = useCallback(() => setReload(previous => previous + 1), []);
  const refreshAfterDelete = useCallback(() => {
    if (result.rows.length === 1 && query.page > 1) setPage(query.page - 1);
    else retry();
  }, [result.rows.length, query.page, setPage, retry]);
  return {
    ...result, rows: result.token === token ? result.rows : [],
    pagination: result.token === token ? result.pagination : emptyPagination,
    filters, setFilters, query, setPage, sortBy, retry, refreshAfterDelete,
    filtersPending: filterKey !== appliedFilterKey,
  };
}

export function useQuoteOptions(token, enabled) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ data: {}, loading: false, error: '', token });
  useEffect(() => {
    if (!enabled || !token) return undefined;
    const cached = getQuoteOptions(token);
    if (cached) {
      setState({ data: cached, loading: false, error: '', token });
      return undefined;
    }
    let active = true;
    setState({ data: {}, loading: true, error: '', token });
    const request = requestQuotes(token, '/filter-options');
    request.promise.then(payload => {
      if (!active || !isCurrentQuoteSession(token)) return;
      cacheQuoteOptions(token, payload.data);
      setState({ data: payload.data, loading: false, error: '', token });
    }).catch(error => {
      if (active && isCurrentQuoteSession(token)) setState({ data: {}, loading: false, error: errorMessage(error), token });
    });
    return () => { active = false; request.release(); };
  }, [token, enabled, attempt]);
  return {
    ...state, data: state.token === token ? state.data : {},
    retry: () => setAttempt(previous => previous + 1),
  };
}

export function useQuoteDetail(token, basicQuote, show) {
  const id = basicQuote?.id;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ id: null, token, data: null, error: '', loading: false });
  useEffect(() => {
    if (!show || !id || !token) return undefined;
    const cached = getQuoteDetail(token, id);
    if (cached) {
      setState({ id, token, data: cached, error: '', loading: false });
      return undefined;
    }
    let active = true;
    setState({ id, token, data: null, error: '', loading: true });
    const request = requestQuotes(token, '/' + id);
    request.promise.then(payload => {
      if (!active || !isCurrentQuoteSession(token)) return;
      cacheQuoteDetail(token, payload.data);
      setState({ id, token, data: payload.data, error: '', loading: false });
    }).catch(error => {
      if (active && isCurrentQuoteSession(token)) setState({ id, token, data: null, error: errorMessage(error), loading: false });
    });
    return () => { active = false; request.release(); };
  }, [token, id, show, attempt]);
  const current = state.id === id && state.token === token;
  const cached = getQuoteDetail(token, id);
  return {
    quote: (current && state.data) || cached || basicQuote,
    loading: show && !cached && (!current || state.loading),
    error: current ? state.error : '',
    retry: () => setAttempt(previous => previous + 1),
  };
}
